// src/screens/SellerScreen.tsx

import React, {
    useContext,
    useState,
    useRef,
    useEffect,
    useCallback,
} from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import NavigationBar, {
    APP_BASE_WIDTH,
    APP_BASE_HEIGHT,
} from '../../nav/NavigationBar'; // ✅ 공통 기준 가져오기
import {
    ChevronLeft,
    Bell,
    Fullscreen,
    ImageOff,
    X,
} from 'lucide-react';

import axios from 'axios';

import { useLocation, useNavigate } from 'react-router-dom';
import { DefaultTokenContext } from '../../context/DefaultTokenContext';
import { SetupContext } from '../../context/SetupContext';

import QRCodeStyling from 'qr-code-styling';

import 'react-datepicker/dist/react-datepicker.css';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store';

import useSecureStorage from '../../hooks/useSecureStorage';
import { tgShowAlert } from '../../utils/telegramPopup';
import QposSocket from '../../nav/QposSocket';

type ViewMode = 'main' | 'notifications' | 'full';

interface ShopInfo {
    name: string;
    imageUrl: string | null;
}

interface NotificationItem {
    id: number;
    pin: string;
    amount: string;
    date: string;
    txHash?: string;
    qpin?: string;
    status?: string;
}

const FOOTER_HEIGHT = 100;
const HEADER_HEIGHT = 52;

// ✅ NavigationBar에서 가져온 공통 기준
const BASE_WIDTH = APP_BASE_WIDTH;
const BASE_HEIGHT = APP_BASE_HEIGHT;

// 전체 레이아웃에서 사용하는 최대 폭은 기준 폭과 동일하게
const MAX_WIDTH = BASE_WIDTH;

const SELL_SEEN_KEY = 'seller_last_sell_key';

// 오늘이면 시:분:초, 아니면 YYYY-MM-DD
const formatNotificationDate = (isoString?: string | null): string => {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '';

    const now = new Date();
    const sameDay =
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate();

    if (sameDay) {
        return d.toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    }

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

// 금액 문자열 포맷팅: 소수점 이하 0 제거
const formatAmountValue = (value: any): string => {
    if (value === null || value === undefined) return '0';
    let s = String(value);
    if (s.includes('.')) {
        s = s.replace(/0+$/, '').replace(/\.$/, '');
        if (s === '') return '0';
    }
    return s;
};

const SellerScreen: React.FC = () => {
    const { theme } = useContext(ThemeContext);
    const sell = useSelector((state: RootState) => state.qpin.sell);

    // ✅ 전체 화면 스케일 비율 (스마트폰에서는 1, 태블릿/PC에서 1보다 커짐)
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const handleResize = () => {
            const vw = window.innerWidth;
            const vh = window.innerHeight;

            const widthRatio = vw / BASE_WIDTH;
            const heightRatio = vh / BASE_HEIGHT;

            const next = Math.min(widthRatio, heightRatio);
            setScale(next);
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [view, setView] = useState<ViewMode>('main');
    const viewRef = useRef<ViewMode>('main');
    useEffect(() => {
        viewRef.current = view;
    }, [view]);

    const [shop, setShop] = useState<ShopInfo>({
        name: '',
        imageUrl: null,
    });

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isModalClosing, setIsModalClosing] = useState(false);
    const [editName, setEditName] = useState(shop.name);
    const [editImageUrl, setEditImageUrl] = useState<string | null>(shop.imageUrl);
    const [editImageFile, setEditImageFile] = useState<File | null>(null);
    const [isSavingShop, setIsSavingShop] = useState(false);

    const [notifClosing, setNotifClosing] = useState(false);

    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [approvedMap, setApprovedMap] = useState<Record<number, boolean>>({});

    const [toastNotifications, setToastNotifications] = useState<NotificationItem[]>([]);
    const [centerAlert, setCenterAlert] = useState<NotificationItem | null>(null);
    const [centerAlertOrigin, setCenterAlertOrigin] = useState<ViewMode | null>(null);
    const [targetPinPos, setTargetPinPos] = useState<{ x: number; y: number } | null>(
        null
    );
    const [autoFlyId, setAutoFlyId] = useState<number | null>(null);
    const [flyAnim, setFlyAnim] = useState<{
        id: number;
        pin: string;
        amount: string;
        dx: number;
        dy: number;
        active: boolean;
    } | null>(null);

    const isMainView = view === 'main';
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const rootRef = useRef<HTMLDivElement | null>(null);
    const firstToastPinRef = useRef<HTMLDivElement | null>(null);
    const notifTopPinRef = useRef<HTMLDivElement | null>(null);
    const completedTopPinRef = useRef<HTMLDivElement | null>(null);

    // QR Code
    const qrCodeRef = useRef<HTMLDivElement | null>(null);
    const qrCodeInstanceRef = useRef<QRCodeStyling | null>(null);
    const hiddenQrRef = useRef<HTMLDivElement | null>(null);
    const qrCodeDownloadInstanceRef = useRef<QRCodeStyling | null>(null);

    const [qrCodeData, setQrCodeData] = useState<{ data: string; image: string }>({
        data: '',
        image: '',
    });
    const [qrPreviewUrl, setQrPreviewUrl] = useState<string | null>(null);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { getWalletAddress, getBlockChain } = useSecureStorage();
    const { searchNetwork } = useContext(DefaultTokenContext);
    const setupContext = useContext(SetupContext);
    const location = useLocation();
    const [token, setToken] = useState(
        location.state === null ? null : (location.state as any).token
    );
    const [address, setAddress] = useState('');
    const [network, setNetwork] = useState('');

    const serverBase = process.env.REACT_APP_QWALLET_SERVER_URL || '';

    // 판매자 알림 목록 조회
    const fetchNotifications = async (sellerWallet: string) => {
        try {
            const res = await axios.post(
                `${serverBase}/qpos/qpin/list`,
                {
                    seller_wallet: sellerWallet,
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            const raw = res.data || {};
            const rows: any[] = Array.isArray(raw.data)
                ? raw.data
                : Array.isArray(raw)
                    ? raw
                    : [];

            const items: NotificationItem[] = rows.map((row, idx) => {
                const txHash = row.tx_hash ?? '';
                const qpin = String(row.qpin ?? '');
                const amt = row.amount ?? 0;
                const status = row.status ?? '';

                const confirmedAt = row.confirmed_at ?? '';
                const verifiedAt = row.tx_verified_at ?? '';
                const createdAt = row.created_at ?? '';

                const baseDate =
                    status === 'CONFIRMED'
                        ? (confirmedAt || verifiedAt || createdAt)
                        : (verifiedAt || createdAt);

                const id = Date.now() + idx;

                return {
                    id,
                    pin: qpin,
                    amount: `${formatAmountValue(amt)} ELYM`,
                    date: formatNotificationDate(baseDate),
                    txHash,
                    qpin,
                    status,
                };
            });

            setNotifications(items);

            const nextApproved: Record<number, boolean> = {};
            items.forEach((item) => {
                nextApproved[item.id] = item.status === 'CONFIRMED';
            });
            setApprovedMap(nextApproved);
        } catch (err) {
            console.error('판매자 알림 목록 조회 실패:', err);
        }
    };

    // 지갑주소 + 네트워크 + 상점정보 + 알림목록
    useEffect(() => {
        const fetchAddressAndShop = async () => {
            try {
                const walletAddress = await getWalletAddress();
                const network = await getBlockChain();

                console.log(`walletAddress : ${walletAddress}`);
                console.log(`network : ${network}`);
                setAddress(walletAddress);
                setNetwork(network);

                const networkInfo = await searchNetwork(network);
                console.log(`newNetworkInfo : ${networkInfo}`);

                let qrIconImage = '';
                if (networkInfo && networkInfo?.icon) {
                    qrIconImage = networkInfo.icon;
                }

                setQrCodeData({
                    data: walletAddress || '',
                    image: qrIconImage,
                });

                if (walletAddress) {
                    try {
                        const res = await axios.post(
                            `${serverBase}/qpos/sales-profile/select`,
                            {
                                wallet_address: walletAddress,
                            },
                            {
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                            }
                        );

                        const data: any = res.data || {};
                        console.log(data);

                        const apiName = data?.data?.name ?? '';
                        const apiImageUrl = data?.data?.image_url ?? '';

                        setShop({
                            name: apiName,
                            imageUrl: apiImageUrl
                                ? `${serverBase}/public${apiImageUrl}`
                                : null,
                        });
                    } catch (err) {
                        console.error('판매자 상점 정보 조회 실패:', err);
                    }

                    await fetchNotifications(walletAddress);
                }
            } catch (err) {
                console.error('지갑/네트워크 정보 조회 실패:', err);
            }
        };
        fetchAddressAndShop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // QRCodeStyling 인스턴스 (화면 표시용)
    useEffect(() => {
        if (!qrCodeInstanceRef.current) {
            qrCodeInstanceRef.current = new QRCodeStyling({
                width: 200,
                height: 200,
                type: 'svg',
                data: qrCodeData.data,
                image: qrCodeData.image,
                imageOptions: {
                    crossOrigin: 'anonymous',
                    margin: 5,
                },
                dotsOptions: {
                    color: theme.qr_img_fill_01,
                },
            });
        } else {
            qrCodeInstanceRef.current.update({
                data: qrCodeData.data,
                image: qrCodeData.image,
                dotsOptions: {
                    color: theme.qr_img_fill_01,
                } as any,
            } as any);
        }
    }, [qrCodeData, theme.qr_img_fill_01]);

    // QRCodeStyling 인스턴스 (다운로드용)
    useEffect(() => {
        if (!qrCodeDownloadInstanceRef.current) {
            qrCodeDownloadInstanceRef.current = new QRCodeStyling({
                width: 250,
                height: 250,
                type: 'svg',
                data: qrCodeData.data,
                image: qrCodeData.image,
                imageOptions: {
                    crossOrigin: 'anonymous',
                    margin: 5,
                },
                dotsOptions: {
                    color: theme.qr_img_fill_01,
                },
            });
        } else {
            qrCodeDownloadInstanceRef.current.update({
                data: qrCodeData.data,
                image: qrCodeData.image,
                dotsOptions: {
                    color: theme.qr_img_fill_01,
                } as any,
            } as any);
        }

        if (hiddenQrRef.current && qrCodeDownloadInstanceRef.current) {
            hiddenQrRef.current.innerHTML = '';
            qrCodeDownloadInstanceRef.current.append(hiddenQrRef.current);
        }
    }, [qrCodeData, theme.qr_img_fill_01]);

    // full 화면에서 QR append + 크기 맞추기
    useEffect(() => {
        if (view === 'full' && qrCodeRef.current && qrCodeInstanceRef.current) {
            const rect = qrCodeRef.current.getBoundingClientRect();
            const size = Math.min(rect.width, rect.height);

            qrCodeInstanceRef.current.update({
                width: size,
                height: size,
            } as any);

            qrCodeRef.current.innerHTML = '';
            qrCodeInstanceRef.current.append(qrCodeRef.current);
        }
    }, [view, qrCodeData]);

    // ===== 스타일 =====
    const root: React.CSSProperties = {
        height: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
        backgroundColor: theme.background_01 ?? '#000000',
        color: theme.text_01 ?? '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
    };

    // ✅ 전체 레이아웃을 통째로 확대/축소하는 래퍼
    const scaleWrapper: React.CSSProperties = {
        width: BASE_WIDTH,
        height: BASE_HEIGHT,
        transform: `scale(${scale})`,
        transformOrigin: 'top center',
        position: 'relative',
    };

    const mainContent: React.CSSProperties = {
        width: '100%',
        maxWidth: MAX_WIDTH,
        height: BASE_HEIGHT - FOOTER_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        padding: '0 16px',
        overflow: 'hidden',
    };

    const header: React.CSSProperties = {
        height: HEADER_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
    };

    const headerLeft: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    };

    const headerSpacer: React.CSSProperties = {
        width: 32,
        height: 32,
    };

    const coinCircle: React.CSSProperties = {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: theme.background_02 ?? '#222',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
    };

    const coinImage: React.CSSProperties = {
        width: 24,
        height: 24,
        objectFit: 'cover',
    };

    const headerTitle: React.CSSProperties = {
        fontSize: 14,
        fontWeight: 500,
        color: theme.text_01 ?? '#fff',
    };

    const headerButtonCircle: React.CSSProperties = {
        width: 32,
        height: 32,
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
    };

    const shopCard: React.CSSProperties = {
        position: 'relative',
        borderRadius: 10,
        backgroundColor: theme.background_02 ?? '#111111',
        padding: 16,
        paddingTop: 16,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        overflow: 'hidden',
    };

    const shopNameBox: React.CSSProperties = {
        flexShrink: 0,
        marginTop: 8,
        marginBottom: 4,
    };

    const shopNameDisplay: React.CSSProperties = {
        boxSizing: 'border-box',
        width: '100%',
        color: theme.text_01 ?? '#ffffff',
        fontSize: 20,
        textAlign: 'center',
    };

    const topActionRow: React.CSSProperties = {
        display: 'flex',
        gap: 12,
    };

    const topActionButton: React.CSSProperties = {
        flex: 1,
        height: 70,
        borderRadius: 14,
        border: `1px solid ${theme.line_01 ?? '##272727'}`,
        backgroundColor: theme.background_03 ?? '#191919',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 4,
        cursor: 'pointer',
    };

    const topActionLabel: React.CSSProperties = {
        fontSize: 13,
        fontWeight: 500,
        color: theme.text_01 ?? '#ffffff',
    };

    const flexibleArea: React.CSSProperties = {
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    };

    const imageBlock: React.CSSProperties = {
        minHeight: 120,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    };

    const shopImageFrame: React.CSSProperties = {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderRadius: 10,
        backgroundColor: shop.imageUrl ? 'transparent' : theme.background_03 ?? '#222',
    };

    const shopImageTag: React.CSSProperties = {
        maxWidth: '100%',
        maxHeight: '100%',
        width: '100%',
        height: 'auto',
        objectFit: 'contain',
        display: 'block',
    };

    const placeholderImageInner: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        color: '#999999',
        fontSize: 12,
    };

    const bottomButtonRow: React.CSSProperties = {
        marginTop: 12,
        display: 'flex',
        gap: 10,
        flexShrink: 0,
    };

    const bottomButtonBase: React.CSSProperties = {
        flex: 1,
        height: 44,
        borderRadius: 16,
        border: 'none',
        outline: 'none',
        fontSize: 15,
        fontWeight: 500,
        cursor: 'pointer',
    };

    const primaryButton: React.CSSProperties = {
        ...bottomButtonBase,
        backgroundColor: '#2f88ff',
        color: '#ffffff',
    };

    const secondaryButton: React.CSSProperties = {
        ...bottomButtonBase,
        backgroundColor: '#3a3a3a',
        color: '#ffffff',
    };

    const notifPageInner: React.CSSProperties = {
        width: '100%',
        maxWidth: MAX_WIDTH,
        height: BASE_HEIGHT,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        margin: '0 auto',
        padding: '0 16px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.background_01 ?? '#000',
        animation: notifClosing
            ? 'seller-notif-none 0s linear forwards'
            : 'seller-notif-slide-in 0.25s ease-out forwards',
        zIndex: 1000,
    };

    const notifContentWrapper: React.CSSProperties = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        marginTop: 8,
        gap: 8,
        minHeight: 0,
    };

    const notifTopSection: React.CSSProperties = {
        flex: 4,
        minHeight: 0,
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        padding: 8,
    };

    const notifListCard: React.CSSProperties = {
        flex: 6,
        minHeight: 0,
        borderRadius: 10,
        backgroundColor: theme.background_02 ?? '#111111',
        padding: '8px 14px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
    };

    const notifCardHeaderRow: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 4,
    };

    const notifCardHeader: React.CSSProperties = {
        fontSize: 13,
        fontWeight: 600,
    };

    const headerActionButton: React.CSSProperties = {
        padding: '4px 10px',
        borderRadius: 12,
        border: 'none',
        outline: 'none',
        fontSize: 11,
        fontWeight: 500,
        cursor: 'pointer',
        backgroundColor: '#252525',
        color: theme.text_01 ?? '#fff',
    };

    const notifListScroll: React.CSSProperties = {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
    };

    const notifRowBase: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        padding: '10px 0',
        borderBottom: `1px solid ${theme.line_01 ?? '#333'}`,
    };

    const notifRowSmall: React.CSSProperties = {
        ...notifRowBase,
        opacity: 0.9,
    };

    const notifPinCircleSmall: React.CSSProperties = {
        width: 35,
        height: 35,
        borderRadius: 16,
        background: 'linear-gradient(135deg,#1f6fff,#2bb5ff)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 15,
        fontWeight: 500,
    };

    const notifLeftArea: React.CSSProperties = {
        width: 52,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: 12,
    };

    const notifMiddleArea: React.CSSProperties = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    };

    const notifAmountSmall: React.CSSProperties = {
        fontSize: 15,
        fontWeight: 500,
    };

    const notifDate: React.CSSProperties = {
        fontSize: 12,
        color: theme.text_03 ?? '#aaa',
    };

    const notifActionArea: React.CSSProperties = {
        minWidth: 60,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    };

    const approveButtonStyle: React.CSSProperties = {
        padding: '7px 14px',
        borderRadius: 16,
        border: 'none',
        outline: 'none',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
        backgroundColor: '#2f88ff',
        color: '#ffffff',
    };

    const completeButton: React.CSSProperties = {
        ...approveButtonStyle,
        backgroundColor: theme.background_03 ?? '#333333',
        color: theme.text_03 ?? '#bbbbbb',
        cursor: 'default',
    };

    const emptyText: React.CSSProperties = {
        fontSize: 12,
        color: theme.text_03 ?? '#777',
        padding: '4px 0',
    };

    const modalOverlay: React.CSSProperties = {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        backgroundColor: 'rgba(0,0,0,0.55)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        zIndex: 2000,
    };

    const modalSheet: React.CSSProperties = {
        width: '100%',
        maxWidth: MAX_WIDTH,
        backgroundColor: theme.background_02 ?? '#111',
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
        padding: '16px 20px 24px',
        boxSizing: 'border-box',
        animation: isModalClosing
            ? 'seller-modal-down 0.25s ease-in forwards'
            : 'seller-modal-up 0.25s ease-out forwards',
        color: theme.text_01 ?? '#ffffff',
        position: 'relative',
    };

    const modalHandle: React.CSSProperties = {
        width: 40,
        height: 4,
        borderRadius: 999,
        backgroundColor: theme.line_01 ?? '#444444',
        alignSelf: 'center',
        margin: '0 auto 5px',
    };

    const modalHeaderRow: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
    };

    const modalTitle: React.CSSProperties = {
        fontSize: 16,
        fontWeight: 600,
    };

    const modalLabel: React.CSSProperties = {
        fontSize: 12,
        marginTop: 12,
        marginBottom: 4,
        color: theme.text_03 ?? '#bbbbbb',
    };

    const modalInput: React.CSSProperties = {
        width: '100%',
        height: 40,
        borderRadius: 10,
        border: 'none',
        backgroundColor: theme.background_03 ?? '#191919',
        color: theme.text_01 ?? '#ffffff',
        padding: '0 12px',
        outline: 'none',
        fontSize: 14,
        boxSizing: 'border-box',
    };

    const shopImageFrameModal: React.CSSProperties = {
        width: '100%',
        height: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderRadius: 10,
        backgroundColor: editImageUrl ? 'transparent' : theme.background_03 ?? '#222',
        marginTop: 4,
    };

    const modalImageButtonRow: React.CSSProperties = {
        marginTop: 10,
        display: 'flex',
        justifyContent: 'flex-end',
    };

    const modalImageButton: React.CSSProperties = {
        height: 32,
        padding: '0 14px',
        borderRadius: 16,
        border: 'none',
        outline: 'none',
        backgroundColor: theme.background_03 ?? '#191919',
        color: theme.text_01 ?? '#ffffff',
        fontSize: 13,
        cursor: 'pointer',
    };

    const modalSaveButton: React.CSSProperties = {
        marginTop: 16,
        width: '100%',
        height: 44,
        borderRadius: 16,
        border: 'none',
        outline: 'none',
        backgroundColor: '#2f88ff',
        color: '#ffffff',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
    };

    const savingOverlay: React.CSSProperties = {
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
    };

    const savingSpinner: React.CSSProperties = {
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '4px solid rgba(255,255,255,0.3)',
        borderTopColor: '#ffffff',
        animation: 'seller-saving-spin 0.8s linear infinite',
    };

    const fullView: React.CSSProperties = {
        position: 'absolute',
        inset: 0,
        width: '100%',
        maxWidth: MAX_WIDTH,
        margin: '0 auto',
        backgroundColor: theme.background_01 ?? '#000000',
        color: theme.text_01 ?? '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        padding: '0 16px',
        zIndex: 1500,
    };

    const fullHeader: React.CSSProperties = {
        height: HEADER_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
    };

    const fullContent: React.CSSProperties = {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 15,
        justifyContent: 'center',
    };

    const fullShopName: React.CSSProperties = {
        textAlign: 'center',
        fontSize: 22,
        fontWeight: 600,
    };

    const fullImageFrame: React.CSSProperties = {
        ...shopImageFrame,
        height: 'auto',
        backgroundColor: shop.imageUrl ? 'transparent' : theme.background_02 ?? '#111111',
    };

    const fullQrWrapper: React.CSSProperties = {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };

    const fullQrFrame: React.CSSProperties = {
        width: '80%',
        aspectRatio: '1 / 1',
        borderRadius: 10,
        backgroundColor: theme.background_02 ?? '#111111',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    const fullBottomSpacer: React.CSSProperties = {
        height: HEADER_HEIGHT,
    };

    const toastContainer: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '90%',
        maxWidth: 380,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none',
        zIndex: 1600,
        paddingTop: 6,
    };

    const toastItem: React.CSSProperties = {
        backgroundColor: '#181a1f',
        borderRadius: 10,
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 6px 16px rgba(0,0,0,0.45)',
        cursor: 'pointer',
        pointerEvents: 'auto',
    };

    const toastPin: React.CSSProperties = {
        minWidth: 42,
        height: 42,
        borderRadius: 16,
        background: 'linear-gradient(135deg,#1f6fff,#2bb5ff)',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 20,
        fontWeight: 700,
        marginRight: 12,
    };

    const toastAmount: React.CSSProperties = {
        flex: 1,
        textAlign: 'right',
        color: '#ffffff',
        fontSize: 20,
        fontWeight: 600,
    };

    const centerAlertCircle: React.CSSProperties = {
        width: 250,
        height: 250,
        borderRadius: '50%',
        backgroundColor: '#115c8e',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
        pointerEvents: 'auto',
        cursor: 'pointer',
        animation: 'center-pulse 1.3s ease-in-out infinite',
    };

    const centerAlertPin: React.CSSProperties = {
        fontSize: 90,
        fontWeight: 700,
    };

    const centerAlertAmount: React.CSSProperties = {
        fontSize: 22,
        fontWeight: 500,
    };

    const flyCircleBase: React.CSSProperties = {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: 250,
        height: 250,
        borderRadius: '50%',
        backgroundColor: '#115c8e',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
        pointerEvents: 'none',
        transform: 'translate(-50%, -50%) scale(1)',
        transition: 'transform 0.35s ease-out, opacity 0.35s ease-out',
        zIndex: 1750,
    };

    const flyCircleLabel: React.CSSProperties = {
        fontSize: 18,
        fontWeight: 600,
        marginBottom: 4,
        opacity: 0.9,
    };

    const flyCirclePin: React.CSSProperties = {
        fontSize: 90,
        fontWeight: 700,
        marginBottom: 6,
    };

    const flyCircleAmount: React.CSSProperties = {
        fontSize: 22,
        fontWeight: 500,
        opacity: 0.9,
    };

    const qrContainer: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `8px solid ${theme.border_02}`,
        width: '100%',
        height: '100%',
        borderRadius: 10,
        boxSizing: 'border-box',
    };

    const qrPreviewOverlay: React.CSSProperties = {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000,
    };

    const qrPreviewInner: React.CSSProperties = {
        maxWidth: '90%',
        maxHeight: '90%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
    };

    const qrPreviewImage: React.CSSProperties = {
        maxWidth: '100%',
        maxHeight: '80%',
        borderRadius: 12,
        backgroundColor: '#fff',
        border: '5px solid #fff',
    };

    const qrPreviewText: React.CSSProperties = {
        fontSize: 14,
        color: '#ffffff',
        textAlign: 'center',
        lineHeight: 1.4,
    };

    const qrPreviewClose: React.CSSProperties = {
        position: 'absolute',
        top: 16,
        right: 16,
        width: 32,
        height: 32,
        borderRadius: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.6)',
        cursor: 'pointer',
    };

    const closeQrPreview = () => {
        if (qrPreviewUrl) {
            URL.revokeObjectURL(qrPreviewUrl);
        }
        setQrPreviewUrl(null);
    };

    // 판매자 정보 등록/수정 호출
    const handleSaveShopInfo = async () => {
        const finalName = editName.trim() || '해피브릿지 붕어빵집';

        if (!address) {
            tgShowAlert?.('지갑 주소를 불러오지 못했습니다.');
            return;
        }

        try {
            const formData = new FormData();
            formData.append('wallet_address', address);
            formData.append('name', finalName);
            if (editImageFile) {
                formData.append('file', editImageFile);
            }

            setIsSavingShop(true);

            await axios.post(`${serverBase}/qpos/sales-profile`, formData);

            setShop({
                name: finalName,
                imageUrl: editImageUrl || null,
            });

            setTimeout(() => {
                setIsSavingShop(false);
                closeModalHandler();
            }, 1000);
        } catch (err) {
            console.error('상점 정보 등록/수정 실패:', err);
            setIsSavingShop(false);
            tgShowAlert?.('상점 정보 저장에 실패했습니다.');
        }
    };

    // QR 다운로드
    const handleDownloadQR = async () => {
        if (!qrCodeDownloadInstanceRef.current || !qrCodeData.data) {
            tgShowAlert?.('QR 코드를 생성할 지갑주소가 없습니다.');
            return;
        }

        try {
            const qrAny = qrCodeDownloadInstanceRef.current as any;
            const blob: Blob | null = await qrAny.getRawData('png');
            if (!blob) {
                throw new Error('QR blob 생성 실패');
            }

            const fileName = '판매자_결제_QR.png';
            const ua = navigator.userAgent || '';
            const isIos = /iPhone|iPad|iPod/i.test(ua);
            const isAndroid = /Android/i.test(ua);
            const nav: any = navigator;

            if (isIos && typeof File !== 'undefined') {
                try {
                    const file = new File([blob], fileName, { type: 'image/png' });
                    if (nav?.canShare && nav.canShare({ files: [file] })) {
                        await nav.share({
                            files: [file],
                            title: 'qWallet 결제 QR',
                            text: '판매자 결제 QR코드를 갤러리에 저장해서 사용 해주세요.',
                        });
                        return;
                    }
                } catch (e) {
                    console.warn('iOS share fallback to download:', e);
                }
            }

            if (isAndroid) {
                const url = URL.createObjectURL(blob);
                setQrPreviewUrl(url);
                return;
            }

            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            try {
                window.open(url, '_blank');
            } catch (e) {
                console.warn('window.open fallback 실패:', e);
            }

            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (err) {
            console.error('QR 다운로드 실패:', err);
            tgShowAlert?.('QR 이미지 다운로드에 실패했습니다.');
        }
    };

    // qPin 승인 API (개별)
    const callApproveApi = async (item: NotificationItem) => {
        if (!item.txHash || !item.qpin) {
            console.warn('승인 API 호출에 필요한 txHash / qpin 없음', item);
            return;
        }
        await axios.post(
            `${serverBase}/qpos/qpin/step3`,
            {
                tx_hash: item.txHash,
                qpin: item.qpin,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );
    };

    // qPin 전체 승인 API (batch)
    const callApproveAllApi = async (sellerWallet: string) => {
        await axios.post(
            `${serverBase}/qpos/qpin/step3/batch`,
            {
                seller_wallet: sellerWallet,
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );
    };

    const handleApprove = async (id: number) => {
        const item = notifications.find((n) => n.id === id);
        if (!item) return;

        try {
            await callApproveApi(item);
            setApprovedMap((prev) => ({ ...prev, [id]: true }));
        } catch (err) {
            console.error('알림 승인 실패:', err);
            tgShowAlert?.('알림 승인에 실패했습니다.');
        }
    };

    const handleCompleteAll = async () => {
        const targets = notifications.filter((n) => !approvedMap[n.id]);
        if (targets.length === 0) return;

        if (!address) {
            tgShowAlert?.('지갑 주소를 불러오지 못했습니다.');
            return;
        }

        try {
            await callApproveAllApi(address);

            setApprovedMap((prev) => {
                const next = { ...prev };
                targets.forEach((item) => {
                    next[item.id] = true;
                });
                return next;
            });
        } catch (err) {
            console.error('전체 승인 실패:', err);
            tgShowAlert?.('전체 승인에 실패했습니다.');
        }
    };

    // sell 소켓 이벤트
    useEffect(() => {
        if (!sell) return;
        if (!address) return;

        const s: any = sell;
        const sellerWallet: string | undefined = s.seller_wallet;
        if (
            sellerWallet &&
            sellerWallet.toLowerCase() !== address.toLowerCase()
        ) {
            return;
        }

        const txHash: string = s.tx_hash ?? '';
        const qpin: string = String(s.qpin ?? '');
        const amountValue = s.amount ?? 0;
        const verifiedAt: string = s.tx_verified_at ?? '';

        try {
            const sellKey = txHash
                ? `${txHash}:${qpin}`
                : JSON.stringify({
                    qpin,
                    amount: amountValue,
                    verifiedAt,
                    sellerWallet: sellerWallet ?? '',
                });

            const lastKey = sessionStorage.getItem(SELL_SEEN_KEY);
            if (lastKey === sellKey) {
                return;
            }
            sessionStorage.setItem(SELL_SEEN_KEY, sellKey);
        } catch (e) {
            console.warn('SELL_SEEN_KEY 처리 중 오류:', e);
        }

        const newId = Date.now();
        const newItem: NotificationItem = {
            id: newId,
            pin: qpin,
            amount: `${formatAmountValue(amountValue)} ELYM`,
            date: formatNotificationDate(verifiedAt),
            txHash,
            qpin,
        };

        setApprovedMap((prev) => ({ ...prev, [newId]: false }));

        const currentView = viewRef.current;

        if (currentView === 'notifications') {
            setCenterAlert(newItem);
            setCenterAlertOrigin('notifications');
        } else {
            setNotifications((prev) => {
                if (
                    txHash &&
                    qpin &&
                    prev.some((n) => n.txHash === txHash && n.qpin === qpin)
                ) {
                    return prev;
                }
                const next = [newItem, ...prev];
                return next.slice(0, 30);
            });

            setCenterAlert(newItem);
            setCenterAlertOrigin(currentView);
        }
    }, [sell, address]);

    // centerAlert 동작 제어
    useEffect(() => {
        if (!centerAlert || !centerAlertOrigin) return;

        const id = centerAlert.id;

        if (centerAlertOrigin === 'full') {
            const timer = setTimeout(() => {
                setCenterAlert((prev) =>
                    prev && prev.id === id ? null : prev
                );
                setCenterAlertOrigin(null);

                setToastNotifications((prev) => {
                    if (prev.some((n) => n.id === id)) return prev;
                    const next = [centerAlert, ...prev];
                    setTimeout(() => {
                        setToastNotifications((p) =>
                            p.filter((x) => x.id !== id)
                        );
                    }, 5000);
                    return next;
                });
            }, 3000);
            return () => clearTimeout(timer);
        }

        if (centerAlertOrigin === 'notifications') {
            const timer = setTimeout(() => {
                setNotifications((prev) => {
                    if (prev.some((n) => n.id === centerAlert.id)) return prev;
                    return [centerAlert, ...prev];
                });
                setCenterAlert((prev) =>
                    prev && prev.id === id ? null : prev
                );
                setCenterAlertOrigin(null);
            }, 5000);
            return () => clearTimeout(timer);
        }

        const timer = setTimeout(() => {
            setCenterAlert((prev) =>
                prev && prev.id === id ? null : prev
            );
            setCenterAlertOrigin(null);
        }, 3000);
        return () => clearTimeout(timer);
    }, [centerAlert, centerAlertOrigin]);

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setEditImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            const result = reader.result as string;
            setEditImageUrl(result);
        };
        reader.readAsDataURL(file);
    };

    // qPin 모달 클릭 → 승인
    const handleCenterAlertClick = async () => {
        if (!centerAlert) return;
        const item = centerAlert;

        try {
            await callApproveApi(item);
        } catch (err) {
            console.error('qPin 확인(승인) 실패:', err);
            tgShowAlert?.('승인 처리에 실패했습니다.');
        }

        setNotifications((prev) => {
            const exists = prev.some(
                (n) =>
                    n.id === item.id ||
                    (n.txHash === item.txHash && n.qpin === item.qpin)
            );
            if (exists) return prev;
            return [item, ...prev];
        });

        setApprovedMap((prev) => ({ ...prev, [item.id]: true }));

        setCenterAlert(null);
        setCenterAlertOrigin(null);
        setToastNotifications((prev) =>
            prev.filter((n) => n.id !== item.id)
        );
    };

    // 토스트 클릭 → 승인
    const handleToastClick = async (n: NotificationItem) => {
        try {
            await callApproveApi(n);
        } catch (err) {
            console.error('토스트 승인 실패:', err);
            tgShowAlert?.('승인 처리에 실패했습니다.');
        }

        setNotifications((prev) => {
            const exists = prev.some(
                (x) =>
                    x.id === n.id ||
                    (x.txHash === n.txHash && x.qpin === n.qpin)
            );
            if (exists) return prev;
            return [n, ...prev];
        });
        setApprovedMap((prev) => ({ ...prev, [n.id]: true }));
        setToastNotifications((prev) =>
            prev.filter((x) => x.id !== n.id)
        );
        setCenterAlert((prev) =>
            prev && prev.id === n.id ? null : prev
        );
    };

    // 플라이 애니메이션 위치 계산
    useEffect(() => {
        if (!rootRef.current) {
            setTargetPinPos(null);
            return;
        }

        const rootRect = rootRef.current.getBoundingClientRect();

        if (view === 'full' && firstToastPinRef.current) {
            const pinRect = firstToastPinRef.current.getBoundingClientRect();
            const x = pinRect.left - rootRect.left + pinRect.width / 2;
            const y = pinRect.top - rootRect.top + pinRect.height / 2;
            setTargetPinPos({ x, y });
        } else if (view === 'notifications') {
            if (autoFlyId === null) {
                setTargetPinPos(null);
                return;
            }

            const isApproved = approvedMap[autoFlyId] === true;

            if (!isApproved) {
                if (notifTopPinRef.current) {
                    const pinRect = notifTopPinRef.current.getBoundingClientRect();
                    const x = pinRect.left - rootRect.left + pinRect.width / 2;
                    const y = pinRect.top - rootRect.top + pinRect.height / 2;
                    setTargetPinPos({ x, y });
                } else {
                    setTargetPinPos(null);
                }
            } else {
                if (completedTopPinRef.current) {
                    const pinRect =
                        completedTopPinRef.current.getBoundingClientRect();
                    const x = pinRect.left - rootRect.left + pinRect.width / 2;
                    const y = pinRect.top - rootRect.top + pinRect.height / 2;
                    setTargetPinPos({ x, y });
                } else {
                    setTargetPinPos(null);
                }
            }
        } else {
            setTargetPinPos(null);
        }
    }, [toastNotifications, notifications, view, autoFlyId, approvedMap]);

    useEffect(() => {
        if (!centerAlert || autoFlyId === null || !targetPinPos || !rootRef.current) {
            return;
        }
        if (centerAlert.id !== autoFlyId) return;

        const rootRect = rootRef.current.getBoundingClientRect();
        const centerX = rootRect.width / 2;
        const centerY = rootRect.height / 2;

        const dx = targetPinPos.x - centerX;
        const dy = targetPinPos.y - centerY;

        setFlyAnim({
            id: centerAlert.id,
            pin: centerAlert.pin,
            amount: centerAlert.amount,
            dx,
            dy,
            active: false,
        });

        setAutoFlyId(null);

        requestAnimationFrame(() => {
            setFlyAnim((prev) => (prev ? { ...prev, active: true } : prev));
        });

        setTimeout(() => {
            setFlyAnim(null);
            setCenterAlert((prev) =>
                prev && prev.id === centerAlert.id ? null : prev
            );
        }, 350);
    }, [centerAlert, autoFlyId, targetPinPos]);

    const closeModalHandler = () => {
        setIsModalClosing(true);
        setTimeout(() => {
            setIsModalClosing(false);
            setIsEditModalOpen(false);
        }, 250);
    };

    // ✅ 뒤로가기 동작 처리: 모달/서브뷰 → 기본뷰, 기본뷰 → /BalanceScreen
    const handleBackNavigation = useCallback(() => {
        // 1) 수정 모달 열려 있으면 모달만 닫기
        if (isEditModalOpen) {
            if (!isSavingShop) {
                closeModalHandler();
            }
            return;
        }

        // 2) full/notifications 같은 서브뷰이면 main으로 복귀
        if (view !== 'main') {
            setView('main');
            setCenterAlert(null);
            setCenterAlertOrigin(null);
            return;
        }

        // 3) 이미 main이면 /BalanceScreen 으로 이동
        navigate('/BalanceScreen');
    }, [isEditModalOpen, isSavingShop, view, navigate]);

    // 브라우저/안드로이드 뒤로가기(popstate)를 항상 가로채기
    useEffect(() => {
        // 첫 진입 시 한 번 쌓아둠
        window.history.pushState(null, '', window.location.href);

        const onPopState = (event: PopStateEvent) => {
            event.preventDefault?.();
            handleBackNavigation();
            // 뒤로가기를 한 번 소비했으므로 다시 현재 화면을 스택에 쌓아줌
            window.history.pushState(null, '', window.location.href);
        };

        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, [handleBackNavigation]);

    const openNotificationsHandler = () => {
        setNotifClosing(false);
        setView('notifications');
    };

    const closeNotificationsHandler = () => {
        setView('main');
    };

    return (
        <>
            <div style={root}>
                <style>{`
        @keyframes seller-modal-up {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes seller-modal-down {
          from { transform: translateY(0);    opacity: 1; }
          to   { transform: translateY(100%); opacity: 0; }
        }
        @keyframes seller-notif-slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes seller-notif-none {
          from { transform: translateX(0); opacity: 1; }
          to   { transform: translateX(0); opacity: 1; }
        }
        @keyframes center-pulse {
          0%   { transform: scale(1); }
          50%  { transform: scale(1.10); }
          100% { transform: scale(1); }
        }
        @keyframes seller-saving-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>

                {/* ✅ 전체 UI를 스마트폰 기준으로 잡고, 큰 화면에서는 비율대로 확대 */}
                <div ref={rootRef} style={scaleWrapper}>
                    {/* 숨겨진 1px QR 컨테이너 */}
                    <div
                        ref={hiddenQrRef}
                        style={{
                            position: 'absolute',
                            width: 1,
                            height: 1,
                            overflow: 'hidden',
                            opacity: 0,
                            pointerEvents: 'none',
                            top: 0,
                            left: 0,
                        }}
                    />

                    {/* 메인 뷰 */}
                    {isMainView && (
                        <div style={mainContent}>
                            <div style={header}>
                                <div style={headerLeft}>
                                    <div style={coinCircle}>
                                        <img
                                            src="/icons/elysium.png"
                                            alt="coin"
                                            style={coinImage}
                                            onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).style.display =
                                                    'none';
                                            }}
                                        />
                                    </div>
                                    <div style={headerTitle}>Elysium</div>
                                </div>
                                <div style={headerSpacer} />
                            </div>

                            <div style={shopCard}>
                                <div style={topActionRow}>
                                    <button
                                        style={topActionButton}
                                        onClick={() => setView('full')}
                                    >
                                        <Fullscreen size={26} color={theme.text_01 ?? '#fff'} />
                                        <span style={topActionLabel}>구매자 화면</span>
                                    </button>
                                    <button
                                        style={topActionButton}
                                        onClick={openNotificationsHandler}
                                    >
                                        <Bell size={26} color={theme.text_01 ?? '#fff'} />
                                        <span style={topActionLabel}>판매자 화면</span>
                                    </button>
                                </div>

                                <div style={shopNameBox}>
                                    <div style={shopNameDisplay}>{shop.name}</div>
                                </div>

                                <div style={flexibleArea}>
                                    <div style={imageBlock}>
                                        <div style={shopImageFrame}>
                                            {shop.imageUrl ? (
                                                <img
                                                    src={shop.imageUrl}
                                                    alt="shop"
                                                    style={shopImageTag}
                                                    onError={(e) => {
                                                        (e.currentTarget as HTMLImageElement).style.display =
                                                            'none';
                                                    }}
                                                />
                                            ) : (
                                                <div style={placeholderImageInner}>
                                                    <ImageOff size={24} />
                                                    <span>이미지 없음</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={bottomButtonRow}>
                                <button
                                    style={primaryButton}
                                    onClick={() => {
                                        setEditName(shop.name);
                                        setEditImageUrl(shop.imageUrl);
                                        setEditImageFile(null);
                                        setIsEditModalOpen(true);
                                    }}
                                >
                                    판매자 정보 수정
                                </button>
                                <button
                                    style={secondaryButton}
                                    onClick={handleDownloadQR}
                                >
                                    QR 다운로드
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 알림 페이지 */}
                    {view === 'notifications' && (
                        <div style={notifPageInner}>
                            <div style={header}>
                                <div
                                    style={headerButtonCircle}
                                    onClick={closeNotificationsHandler}
                                >
                                    <ChevronLeft size={20} color={theme.text_01 ?? '#fff'} />
                                </div>
                                <div
                                    style={{
                                        flex: 1,
                                        textAlign: 'center',
                                        fontSize: 16,
                                        fontWeight: 600,
                                    }}
                                >
                                    알림
                                </div>
                                <div style={headerSpacer} />
                            </div>

                            <div style={notifContentWrapper}>
                                {(() => {
                                    const pending = notifications.filter(
                                        (item) => !approvedMap[item.id]
                                    );
                                    const completed = notifications.filter(
                                        (item) => !!approvedMap[item.id]
                                    );
                                    const visible = [...pending, ...completed];

                                    return (
                                        <>
                                            {/* qPin 영역 */}
                                            <div style={notifTopSection}>
                                                {centerAlert && !flyAnim ? (
                                                    <div
                                                        style={{
                                                            ...centerAlertCircle,
                                                            width: 230,
                                                            height: 230,
                                                            position: 'relative',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                        onClick={handleCenterAlertClick}
                                                    >
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: 20,
                                                                fontSize: 20,
                                                            }}
                                                        >
                                                            qPin
                                                        </div>
                                                        <div
                                                            style={{
                                                                fontSize: 90,
                                                                fontWeight: 700,
                                                            }}
                                                        >
                                                            {centerAlert.pin}
                                                        </div>
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                bottom: 20,
                                                                fontSize: 22,
                                                            }}
                                                        >
                                                            {centerAlert.amount}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: 8,
                                                        }}
                                                    >
                                                        <button
                                                            style={{
                                                                ...headerActionButton,
                                                                padding: '8px 16px',
                                                                fontSize: 12,
                                                            }}
                                                            onClick={handleCompleteAll}
                                                        >
                                                            전체 승인
                                                        </button>
                                                        <span
                                                            style={{
                                                                fontSize: 13,
                                                                color: theme.text_03,
                                                            }}
                                                        >
                                                            신규 알람 대기 중입니다.
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* 알림 리스트 */}
                                            <div style={notifListCard}>
                                                <div style={notifCardHeaderRow}>
                                                    <span style={notifCardHeader}>알림 목록</span>
                                                </div>
                                                <div style={notifListScroll}>
                                                    {visible.length === 0 && (
                                                        <div style={emptyText}>
                                                            리스트가 없습니다.
                                                        </div>
                                                    )}
                                                    {visible.map((item, index) => {
                                                        const isApproved =
                                                            !!approvedMap[item.id];
                                                        const isLast =
                                                            index ===
                                                            visible.length - 1;

                                                        let ref: React.RefObject<HTMLDivElement> | null =
                                                            null;
                                                        if (!isApproved) {
                                                            if (pending[0] && pending[0].id === item.id) {
                                                                ref = notifTopPinRef;
                                                            }
                                                        } else {
                                                            if (
                                                                completed[0] &&
                                                                completed[0].id === item.id
                                                            ) {
                                                                ref = completedTopPinRef;
                                                            }
                                                        }

                                                        const isBoundaryLine =
                                                            pending.length > 0 &&
                                                            completed.length > 0 &&
                                                            pending[pending.length - 1]?.id ===
                                                            item.id;

                                                        return (
                                                            <div
                                                                key={item.id}
                                                                style={{
                                                                    ...notifRowSmall,
                                                                    borderBottom: isBoundaryLine
                                                                        ? `2px solid ${theme.line_01 ?? '#333'
                                                                        }`
                                                                        : isLast
                                                                            ? 'none'
                                                                            : 'none',
                                                                }}
                                                            >
                                                                <div style={notifLeftArea}>
                                                                    <div
                                                                        ref={ref}
                                                                        style={notifPinCircleSmall}
                                                                    >
                                                                        {item.pin}
                                                                    </div>
                                                                </div>
                                                                <div style={notifMiddleArea}>
                                                                    <span style={notifAmountSmall}>
                                                                        {item.amount}
                                                                    </span>
                                                                    <span style={notifDate}>
                                                                        {item.date}
                                                                    </span>
                                                                </div>
                                                                <div style={notifActionArea}>
                                                                    <button
                                                                        style={
                                                                            isApproved
                                                                                ? completeButton
                                                                                : approveButtonStyle
                                                                        }
                                                                        onClick={() =>
                                                                            !isApproved &&
                                                                            handleApprove(item.id)
                                                                        }
                                                                        disabled={isApproved}
                                                                    >
                                                                        {isApproved ? '완료' : '승인'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}

                    {/* 전환 페이지 (full) */}
                    {view === 'full' && (
                        <div style={fullView}>
                            {toastNotifications.length > 0 && (
                                <div style={toastContainer}>
                                    {toastNotifications.map((n, index) => (
                                        <div
                                            key={n.id}
                                            style={toastItem}
                                            onClick={() => handleToastClick(n)}
                                        >
                                            <div
                                                ref={index === 0 ? firstToastPinRef : null}
                                                style={toastPin}
                                            >
                                                {n.pin}
                                            </div>
                                            <div style={toastAmount}>{n.amount}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div style={fullHeader}>
                                <div
                                    style={headerButtonCircle}
                                    onClick={() => setView('main')}
                                >
                                    {/* <X size={20} color={theme.text_01 ?? '#fff'} /> */}
                                    <ChevronLeft size={20} color={'#fff'} />
                                </div>
                            </div>

                            <div style={fullContent}>
                                <div style={fullShopName}>{shop.name}</div>

                                {shop.imageUrl && (
                                    <div style={fullImageFrame}>
                                        <img
                                            src={shop.imageUrl}
                                            alt="shop"
                                            style={shopImageTag}
                                            onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).style.display =
                                                    'none';
                                            }}
                                        />
                                    </div>
                                )}

                                <div style={fullQrWrapper}>
                                    <div style={fullQrFrame}>
                                        <div style={qrContainer} ref={qrCodeRef}></div>
                                    </div>

                                    {centerAlert && view === 'full' && !flyAnim && (
                                        <div
                                            style={{
                                                ...centerAlertCircle,
                                                position: 'absolute',
                                            }}
                                            onClick={handleCenterAlertClick}
                                        >
                                            <div
                                                style={{
                                                    fontSize: 20,
                                                    fontWeight: 600,
                                                    marginBottom: 4,
                                                }}
                                            >
                                                qPin
                                            </div>
                                            <div style={centerAlertPin}>
                                                {centerAlert.pin}
                                            </div>
                                            <div style={centerAlertAmount}>
                                                {centerAlert.amount}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div style={fullBottomSpacer} />
                            </div>
                        </div>
                    )}

                    {/* qPin 애니메이션 원 */}
                    {flyAnim && (
                        <div
                            style={{
                                ...flyCircleBase,
                                transform: flyAnim.active
                                    ? `translate(calc(-50% + ${flyAnim.dx}px), calc(-50% + ${flyAnim.dy}px)) scale(0.3)`
                                    : 'translate(-50%, -50%) scale(1)',
                                opacity: flyAnim.active ? 0 : 1,
                            }}
                        >
                            <div style={flyCircleLabel}>qPin</div>
                            <div style={flyCirclePin}>{flyAnim.pin}</div>
                            <div style={flyCircleAmount}>{flyAnim.amount}</div>
                        </div>
                    )}

                    {/* 수정 모달 */}
                    {isEditModalOpen && (
                        <div
                            style={modalOverlay}
                            onClick={() => {
                                if (!isSavingShop) closeModalHandler();
                            }}
                        >
                            <div
                                style={modalSheet}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div
                                    style={modalHandle}
                                    onClick={() => {
                                        if (!isSavingShop) closeModalHandler();
                                    }}
                                />
                                <div style={modalHeaderRow}>
                                    <span style={modalTitle}>상점 정보 수정</span>
                                    <button
                                        style={{
                                            ...headerButtonCircle,
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                        }}
                                        onClick={closeModalHandler}
                                        disabled={isSavingShop}
                                    >
                                        <X size={18} color={theme.text_01 ?? '#fff'} />
                                    </button>
                                </div>

                                <div>
                                    <div style={modalLabel}>상점명</div>
                                    <input
                                        style={modalInput}
                                        placeholder="상점명을 입력해주세요"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        disabled={isSavingShop}
                                    />
                                </div>

                                <div style={modalLabel}>상점이미지</div>
                                <div style={shopImageFrameModal}>
                                    {editImageUrl ? (
                                        <img
                                            src={editImageUrl}
                                            alt="edit shop"
                                            style={shopImageTag}
                                            onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).style.display =
                                                    'none';
                                            }}
                                        />
                                    ) : (
                                        <div style={placeholderImageInner}>
                                            <ImageOff size={24} />
                                            <span>이미지 없음</span>
                                        </div>
                                    )}
                                </div>
                                <div style={modalImageButtonRow}>
                                    <button
                                        style={modalImageButton}
                                        onClick={() => !isSavingShop && fileInputRef.current?.click()}
                                        disabled={isSavingShop}
                                    >
                                        이미지 업로드
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleImageUpload}
                                    />
                                </div>

                                <button
                                    style={modalSaveButton}
                                    onClick={handleSaveShopInfo}
                                    disabled={isSavingShop}
                                >
                                    수정하기
                                </button>

                                {isSavingShop && (
                                    <div style={savingOverlay}>
                                        <div style={savingSpinner} />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 안드로이드용 QR 전체 화면 미리보기 */}
                    {qrPreviewUrl && (
                        <div style={qrPreviewOverlay} onClick={closeQrPreview}>
                            <div style={qrPreviewInner} onClick={(e) => e.stopPropagation()}>
                                <div style={qrPreviewClose} onClick={closeQrPreview}>
                                    <X size={20} color="#ffffff" />
                                </div>
                                <img
                                    src={qrPreviewUrl}
                                    alt="QR preview"
                                    style={qrPreviewImage}
                                />
                                <div style={qrPreviewText}>
                                    이미지를 길게 눌러서<br />
                                    기기 갤러리에 저장하세요.
                                </div>
                            </div>
                        </div>
                    )}
                    <QposSocket />
                </div>
            </div>

            {/* 메인 화면에서만 풋터 노출 */}
            {isMainView && (
                <div
                    style={{
                        width: '100%',
                        maxWidth: MAX_WIDTH,
                        height: FOOTER_HEIGHT,
                    }}
                >
                    <NavigationBar />
                </div>
            )}
        </>
    );
};

export default SellerScreen;
